/**
 * Centralized API instance with authentication handling
 *
 * This module provides a configured axios instance that:
 * - Automatically adds Authorization header from localStorage
 * - Handles 401 Unauthorized errors by showing an alert and redirecting to login
 * - Provides consistent error handling across all API calls
 */

import axios from 'axios';
import { message } from 'antd';

// Create axios instance
const api = axios.create({
  withCredentials: true,
  timeout: 30000,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear stored auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('username');

      // Show authorization error message
      message.error('Authorization Error: Your session has expired. Please login again.');

      // Redirect to login page after a short delay to let the message show
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);

      // Return a rejected promise with a clear error
      return Promise.reject(new Error('Unauthorized - redirecting to login'));
    }

    return Promise.reject(error);
  }
);

/**
 * Handle 401 error manually (for non-axios calls)
 * Call this when you detect a 401 error from other sources
 */
export const handleUnauthorized = () => {
  // Clear stored auth data
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('username');

  // Show authorization error message
  message.error('Authorization Error: Your session has expired. Please login again.');

  // Redirect to login page
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

export default api;
